import _ from 'lodash';

import { getGravatarUrl, andify } from '@zapier/common-utils';
import {
  EntityActions,
  commonErrors as Errors,
  buildEntityActions,
  buildEntityApi,
  buildEntitySelectors,
  entityUtils,
  hasErrorsOfType,
} from '@zapier/toolbox-redux-framework';
import { addTruthyParamsToUrl, stripTrailingSlash } from '@zapier/url-utils';
import { daysFromNow, getClientTimeZone } from '@zapier/date';
import { fetch, postJson } from '@zapier/toolbox-browser-fetch';
import {
  isFullStoryInstalled,
  setFullStoryUserVars,
} from '@zapier/toolbox-tracking';

import * as Account from './Account';
import { getIdentity } from '../utils/IdentityUtils';
import { getSwitchesFromState } from '../utils/SwitchesUtils';
import partialWithDefault from '../utils/partialWithDefault';

export const type = 'profile';

const identity = getIdentity();

export const api = buildEntityApi(
  type,
  path => {
    return addTruthyParamsToUrl(
      stripTrailingSlash(`/api/v3/profiles/${path}`),
      { identity }
    );
  },
  profileApi => {
    const { loadCollection } = profileApi;

    profileApi.loadCollection = (...args) =>
      loadCollection(...args).catch(
        _.partial(entityUtils.remove401StatusFromGetErrorResponse, type)
      );

    profileApi.createThroughGoogleOneTap = async data => {
      try {
        const response = await postJson('/google-sso/onetap/', data);
        return response;
      } catch (e) {
        // postJson doesn't parse the JSON response when there is an error code
        // and leaves the stream locked so you have to manually process responseText
        const json = JSON.parse(e.responseText);
        // If the login failed because of SAML then the sso_url will be returned with
        // the error text
        if (json.sso_url) {
          // Redirect to the sso_url so the user can log in with SAML
          window.location = json.sso_url;
        }
      }
      return null;
    };

    profileApi.create = data => {
      // "Trying too often" type failures 401 so we need `autoPromiseMiddleware`
      // to ignore those and not redirect to login while trying to sign up.
      return postJson('/api/v3/profiles', data).catch(
        _.partial(entityUtils.remove401StatusFromGetErrorResponse, type)
      );
    };

    const normalizeGDPRSuccess = async response => {
      try {
        const message = await response.json();
        return {
          status: 'success',
          statusCode: response.status,
          // if it's a 404, there's basically nothing to report,
          // so don't update the UI unnecessarily for that. For all
          // other error messages though, we should let them know what
          // happened.
          // the notification needs to be separate from the
          // status.
          ...message,
        };
      } catch (err) {
        return {
          status: 'success',
        };
      }
    };

    const normalizeGDPRError = async response => {
      try {
        const message = response.bodyUsed
          ? JSON.parse(response.responseText)
          : await response.json();
        return {
          status: 'error',
          statusCode: response.status,
          error: _.head(message.errors),
        };
      } catch (err) {
        return {
          status: 'error',
          statusCode: response.status,
          error: err,
        };
      }
    };

    profileApi.checkGDPRItemStatus = (actionType, id) => {
      return fetch(profileApi.modelEndpoint(`${id}/data/${actionType}`))
        .then(normalizeGDPRSuccess)
        .catch(normalizeGDPRError);
    };

    profileApi.checkExportStatus = _.partial(
      profileApi.checkGDPRItemStatus,
      'export'
    );

    profileApi.checkDeleteStatus = _.partial(
      profileApi.checkGDPRItemStatus,
      'delete'
    );

    profileApi.runGDPRAction = (actionType, id) => {
      return fetch(profileApi.modelEndpoint(`${id}/data/${actionType}`), {
        method: 'POST',
      })
        .then(normalizeGDPRSuccess)
        .catch(normalizeGDPRError);
    };

    profileApi.exportData = _.partial(profileApi.runGDPRAction, 'export');

    profileApi.deleteData = _.partial(profileApi.runGDPRAction, 'delete');
  }
);

export const ALERT_ACTIONS = 'alert_actions';
export const ALERT_TRIGGERS = 'alert_triggers';
export const AVATAR_URL = 'avatar'; // client-side only
export const DISABLE_SSL = 'disable_ssl_check';
export const EMAIL = 'email';
export const EMAIL_CONFIRMED = 'email_confirmed';
export const FIRST_NAME = 'first_name';
export const LAST_NAME = 'last_name';
export const IS_DEVELOPER = 'is_developer';
export const NEW_PASSWORD = 'password1';
export const NEW_PASSWORD_CONFIRM = 'password2';
export const PASSWORD_VERIFY = 'password';
export const SUMMARY_SCHEDULE = 'summary_schedule';
export const TIMEZONE = 'timezone';
export const TOS_AGREEMENT = 'tos_agreement';
export const SHOULD_RENEW_TOS = 'should_renew_tos';
export const EMAIL_PENDING_CHANGE = 'email_pending_change';
export const EMAIL_CONFIRMATION_SENT = 'email_confirm_sent';
export const VIEWED_HELP = 'viewed_help';
export const LOADING = 'Loading…';
export const GOOGLE_ONE_TAP_VERIFY_MESSAGE = 'account_verification_required';
export const GOOGLE_ONE_TAP_NEW_ENTITY_ID = 'new-profile-google-one-tap';
export const PERSONAS = 'personas';
export const JOB_TITLE = 'job_title';
export const PRIMARY_ORGANIZATION = 'primary_organization';

export const selectors = buildEntitySelectors(type, sel => {
  sel.code = _.partial(sel.dataItem, 'code');
  sel.email = _.partial(sel.dataItem, EMAIL);
  sel.emailHash = _.partial(sel.dataItem, 'email_hash');
  sel.entrustedAppIds = _.partial(sel.dataItem, 'entrusted_app_ids');
  sel.gravatarUrl = _.flow(sel.emailHash, getGravatarUrl);
  sel.isEmailConfirmed = _.partial(sel.dataItem, EMAIL_CONFIRMED);
  sel.isEmailPendingChange = _.partial(sel.stateItem, EMAIL_PENDING_CHANGE);
  sel.isEmailConfirmationSent = _.partial(
    sel.stateItem,
    EMAIL_CONFIRMATION_SENT
  );
  sel.firstName = partialWithDefault(sel.dataItem, [FIRST_NAME], '');
  sel.lastName = partialWithDefault(sel.dataItem, [LAST_NAME], '');
  sel.timezone = _.partial(sel.dataItem, TIMEZONE);

  sel.viewedHelp = _.partial(sel.dataItem, VIEWED_HELP);
  sel.hasViewedHelp = (key, entity) =>
    _.get(sel.viewedHelp(entity), key) === true;
  sel.seenNotifications = partialWithDefault(
    sel.dataItem,
    ['has_seen_notifications'],
    {}
  );
  sel.hasSeenNotification = (key, entity) =>
    _.get(sel.seenNotifications(entity), key) === true;
  sel.normalizedName = entity =>
    `${sel.firstName(entity)} ${sel.lastName(entity)}`.trim() ||
    sel.email(entity);
  sel.normalizedNameOrLoading = entity => sel.normalizedName(entity) || LOADING;
  sel.signup = _.partial(sel.dataItem, 'signup');
  sel.hasSignedUpInLastDays = (entity, days) =>
    daysFromNow(sel.signup(entity)) <= days;
  sel.hasActivated = _.partial(sel.dataItem, 'has_activated');
  sel.bannerMessage = partialWithDefault(sel.dataItem, ['banner_message'], '');

  sel.exportData = _.partial(sel.dataItem, 'export_data');
  sel.exportDataStatus = entity => _.get(sel.exportData(entity), 'status');
  sel.exportDataMessage = entity => _.get(sel.exportData(entity), 'message');
  sel.isExportInProgress = entity =>
    _.get(sel.exportData(entity), 'in_progress');
  sel.isExportComplete = entity => _.get(sel.exportData(entity), 'is_complete');

  sel.deleteData = _.partial(sel.dataItem, 'delete_data');
  sel.deleteDataStatus = entity => _.get(sel.deleteData(entity), 'status');
  sel.deleteDataMessage = entity => _.get(sel.deleteData(entity), 'message');
  sel.isDeleteInProgress = entity =>
    _.get(sel.deleteData(entity), 'in_progress');
  sel.isDeleteComplete = entity => _.get(sel.deleteData(entity), 'is_complete');

  // Is the user timezone mismatching his computer's one?
  sel.isTimezoneMismatching = entity => {
    const profileTimeZone = sel.timezone(entity);
    const clientTimeZone = getClientTimeZone();

    return profileTimeZone ? profileTimeZone !== clientTimeZone : false;
  };
  sel.roles = _.partial(sel.dataItem, 'roles');

  sel.all.currentProfileId = _.partial(sel.all.dataItem, 'active_id');
  sel.all.currentProfile = state =>
    sel.all.entity(sel.all.currentProfileId(state), state);
  // This uses `Account` but the tracking key is for `Profile`,
  // so it's a `Profile` selector.
  sel.all.currentAccountDowngradedTrackingKey = state =>
    `account-downgraded-${Account.selectors.all.currentAccountId(state)}`;
  sel.all.isLoggedIn = state => !!sel.all.currentProfile(state);
  sel.all.shouldLoad = state => {
    // When we hydrate the state of the users logged in status from the backend
    // we do not hydrate all of the information. Instead we only send enough
    // info for the application to know it is logged in (see header.html).
    // This check here ensures that we then request the full profile data once
    // we are on the client.
    if (sel.all.isPartiallyHydrated(state)) {
      return true;
    }

    return andify(
      _.negate(sel.all.isFailure),
      _.negate(sel.all.isLoggedIn)
    )(state);
  };
  sel.all.googleOneTapEntity = _.partial(
    sel.all.entity,
    GOOGLE_ONE_TAP_NEW_ENTITY_ID
  );
  sel.all.needsGoogleSSOAccountVerification = state =>
    _.includes(
      sel.statusMessages(sel.all.googleOneTapEntity(state)),
      GOOGLE_ONE_TAP_VERIFY_MESSAGE
    );
  // Google One tap might have an error stating that it needs to verify the
  // account. That will be handled by `needsGoogleSSOAccountVerification`. This
  // is for detecting any other error that may occur.
  sel.all.hasGoogleOneTapError = state => {
    const isGoogleOneTapErrored = sel.isFailure(
      sel.all.googleOneTapEntity(state)
    );
    return Boolean(
      isGoogleOneTapErrored && !sel.all.needsGoogleSSOAccountVerification(state)
    );
  };

  sel.all.isAdminOf = (accountId, state) => {
    const currentProfile = sel.all.currentProfile(state);
    const roles = sel.roles(currentProfile);
    if (Array.isArray(roles)) {
      return roles.some(
        r =>
          r.account_id === accountId &&
          (r.role === 'owner' || r.role === 'admin' || r.role === 'super_admin')
      );
    }
    return false;
  };

  sel.all.isSuperAdminOf = (accountId, state) => {
    const currentProfile = sel.all.currentProfile(state);
    const roles = sel.roles(currentProfile);
    if (Array.isArray(roles)) {
      return roles.some(
        r =>
          r.account_id === accountId &&
          (r.role === 'owner' || r.role === 'super_admin')
      );
    }
    return false;
  };

  sel.all.isOwnerOf = (accountId, state) => {
    const currentProfile = sel.all.currentProfile(state);
    const roles = sel.roles(currentProfile);
    if (Array.isArray(roles)) {
      return roles.some(r => r.account_id === accountId && r.role === 'owner');
    }
    return false;
  };

  sel.all.isMasquerading = partialWithDefault(
    sel.all.dataItem,
    ['is_masquerading'],
    false
  );

  sel.all.isAdmin = state => {
    const accountId = Account.selectors.all.currentAccountId(state);
    return sel.all.isAdminOf(accountId, state);
  };

  sel.all.hasEnableManageTeamTab = () => true;
});

export const utils = {
  createSignupError(profile) {
    const errors = selectors.statusMessages(profile);
    if (hasErrorsOfType('field_errors', errors)) {
      return new Errors.SignUpValidationError({ errors });
    } else {
      return new Errors.SignUpError({ errors });
    }
  },
};

export const actions = buildEntityActions(
  type,
  selectors,
  api,
  entityActions => {
    const overrideLoad = originalActionCreator => {
      return (...args) => (dispatch, getState) => {
        return dispatch(originalActionCreator(...args)).then(() => {
          const state = getState();

          if (isFullStoryInstalled()) {
            const switches = getSwitchesFromState(state);
            setFullStoryUserVars({
              switches_str: JSON.stringify(switches),
            });
          }
        });
      };
    };

    entityActions.loadCollection = overrideLoad(entityActions.loadCollection);
    entityActions.forceLoadCollection = overrideLoad(
      entityActions.forceLoadCollection
    );

    const getEntityData = (id, state) =>
      selectors.data(selectors.all.entity(id, state));

    entityActions.createThroughGoogleOneTap = (data, tempId) =>
      EntityActions.createEntity(
        type,
        () => data,
        api.createThroughGoogleOneTap,
        null,
        tempId
      );

    entityActions.saveProfile = (messages, entityId) =>
      EntityActions.saveEntity(
        type,
        getEntityData,
        api.save,
        messages,
        entityId
      );

    entityActions.acknowledgeHelp = (trackingKey, id) =>
      EntityActions.saveEntity(
        type,
        () => ({ [VIEWED_HELP]: { [trackingKey]: true } }),
        api.save,
        null,
        id
      );

    // /api/v3/profiles/XXX/data/delete or
    // /api/v3/profiles/XXX/data/export

    // 403: the feature is disabled by gargoyle
    // 404: feature is active but never been used
    // 409: feature already started
    // 202: feature is in progress
    // 200: feature is complete

    entityActions.updateGDPRData = (actionType, data) => {
      return (dispatch, getState) => {
        const profileId = selectors.all.currentProfileId(getState());
        const currentProfile = selectors.all.currentProfile(getState());
        const selectData = {
          export: selectors.exportData,
          delete: selectors.deleteData,
        }[actionType];
        const existingEntityData = selectData(currentProfile);
        const newData = {
          ...existingEntityData,
          ...data,
          // these keys are used specifically for the notification
          // on the page. Status must be either 'success' or 'failure'
          // and the explanation is the message.
          message: data.message || data.error,
          status: data.status,
        };
        dispatch(
          entityActions.updateEntityDataItem(
            `${actionType}_data`,
            newData,
            profileId
          )
        );
      };
    };

    entityActions.updateGDPRExportData = data => {
      return entityActions.updateGDPRData('export', data);
    };

    entityActions.updateGDPRDeleteData = data => {
      return entityActions.updateGDPRData('delete', data);
    };

    const GDPRApiFunction = {
      export: api.exportData,
      delete: api.deleteData,
    };

    const updateGDPRItemFunction = {
      export: entityActions.updateGDPRExportData,
      delete: entityActions.updateGDPRDeleteData,
    };

    entityActions.runGDPRAction = actionType => {
      return (dispatch, getState) => {
        const profileId = selectors.all.currentProfileId(getState());

        GDPRApiFunction[actionType](profileId).then(
          ({ status, statusCode, message, error }) => {
            dispatch(
              updateGDPRItemFunction[actionType]({
                status,
                // this will be either 201 or 202 depending on whether it's
                // a POST or GET respectively
                in_progress: statusCode === 201 || statusCode === 202,
                is_completed: statusCode === 200,
                message,
                error,
              })
            );
          }
        );
      };
    };

    entityActions.deleteData = () => entityActions.runGDPRAction('delete');

    entityActions.exportData = () => entityActions.runGDPRAction('export');

    entityActions.checkGDPRStatus = actionType => {
      const apiFunction = {
        export: api.checkExportStatus,
        delete: api.checkDeleteStatus,
      }[actionType];

      return (dispatch, getState) => {
        const profileId = selectors.all.currentProfileId(getState());
        apiFunction(profileId).then(data => {
          dispatch(
            updateGDPRItemFunction[actionType]({
              // this will be either 201 or 202 depending on whether it's
              // a POST or GET respectively
              in_progress: data.statusCode === 201 || data.statusCode === 202,
              is_completed: data.statusCode === 200,
            })
          );
        });
      };
    };

    entityActions.checkExportStatus = () =>
      entityActions.checkGDPRStatus('export');

    entityActions.checkDeleteStatus = () =>
      entityActions.checkGDPRStatus('delete');

    entityActions.clearGDPRNotification = actionType =>
      updateGDPRItemFunction[actionType]({ status: null, message: null });

    entityActions.clearDeleteStatus = () =>
      entityActions.clearGDPRNotification('delete');

    entityActions.clearExportStatus = () =>
      entityActions.clearGDPRNotification('export');
  }
);
